// Shared navigation config: the category/subheading data plus a pure helper to
// resolve the active section+item from the current pathname. Kept free of React
// and the auth store so it stays trivially testable and importable by both the
// sidebar (NavMenu, categories) and the horizontal selector (SectionTabs,
// subheadings). Permission filtering with can() happens in the consumers.

export interface NavItem {
  to: string;
  label: string;
  /**
   * Permission required to see this link. Omit for always-visible items. An
   * array means "any of" — the item shows if the user holds at least one (used
   * where a page draws from two domains, e.g. Directory › Individuals, which
   * merges org.read clients with principal.read protectees).
   */
  perm?: string | string[];
  /** Inline icon (emoji or short glyph) rendered before the label. */
  icon?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
  /** Optional category glyph for the sidebar row; falls back to items[0].icon. */
  icon?: string;
}

// Sidebar navigation. Domain links are permission-gated via can() so an
// officer never sees finance/admin sections they cannot open.
//
// DOMAIN NAV LINKS INSERTED IN INTEGRATION
// Each domain module appends its NavSection(s) here once its pages exist.
// Keep groups ordered by operational frequency (live ops first, admin last).
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/app', label: 'Dashboard', icon: '▣' }],
  },
  {
    label: 'My Workspace',
    items: [
      { to: '/me/profile', label: 'My Profile', perm: 'self.read_profile', icon: '🪞' },
      { to: '/me/schedule', label: 'My Schedule', perm: 'self.read_schedule', icon: '📅' },
      { to: '/me/timesheets', label: 'My Timesheets', perm: 'self.read_timesheets', icon: '🕒' },
      { to: '/me/credentials', label: 'My Credentials', perm: 'self.read_credentials', icon: '🎖' },
    ],
  },
  {
    label: 'Webmail',
    icon: '✉',
    items: [{ to: '/me/webmail', label: 'Webmail', icon: '✉' }],
  },
  {
    label: 'Operations',
    items: [
      { to: '/schedule', label: 'Schedule', perm: 'assignment.read', icon: '🗓' },
      { to: '/timesheets', label: 'Timesheets', perm: 'timesheet.read', icon: '⏱' },
      { to: '/incidents', label: 'Incidents', perm: 'incident.read', icon: '⚠' },
      { to: '/incidents/review', label: 'Incident Review', perm: 'incident.review', icon: '✓' },
      { to: '/dars', label: 'Daily Activity Reports', perm: 'dar.read', icon: '📝' },
      { to: '/visitors', label: 'Visitors', perm: 'visitor.read', icon: '🚪' },
    ],
  },
  {
    // Internal workforce only — guards, agents, staff, and contractors
    // (contractors are Personnel with employment_type = 'contractor'). The
    // platform-wide persons registry (/persons) lives outside the nav.
    label: 'Staff',
    items: [
      { to: '/personnel', label: 'Personnel', perm: 'personnel.read', icon: '👮' },
      { to: '/credentials/expiring', label: 'Credential Expiry', perm: 'credential.read', icon: '🪪' },
    ],
  },
  {
    // Client directory: Individuals (HNI-type client orgs + protected
    // principals) and Businesses (all other client orgs), plus Sites.
    label: 'Directory',
    items: [
      { to: '/directory/individuals', label: 'Individuals', perm: ['org.read', 'principal.read'], icon: '👤' },
      { to: '/directory/businesses', label: 'Businesses', perm: 'org.read', icon: '🏢' },
      { to: '/sites', label: 'Sites', perm: 'site.read', icon: '⬡' },
    ],
  },
  {
    label: 'Executive Protection',
    items: [
      { to: '/details', label: 'Protective Details', perm: 'detail.read', icon: '🛡' },
      { to: '/surveys', label: 'Site Surveys', perm: 'survey.read', icon: '🔍' },
    ],
  },
  {
    label: 'Assets',
    items: [{ to: '/assets', label: 'Asset Inventory', perm: 'asset.read', icon: '📦' }],
  },
  {
    label: 'Billing',
    items: [
      { to: '/app/invoices', label: 'Invoices', perm: 'invoice.read', icon: '🧾' },
      { to: '/contracts', label: 'Contracts', perm: 'contract.read', icon: '📄' },
      { to: '/billing-rates', label: 'Billing Rates', perm: 'billing_rate.read', icon: '💲' },
    ],
  },
  {
    label: 'Financials',
    items: [
      { to: '/projects', label: 'Projects', perm: 'project.read', icon: '📁' },
      { to: '/expenses', label: 'Expenses', perm: 'expense.read', icon: '💸' },
      { to: '/income', label: 'Income', perm: 'payment.read', icon: '💰' },
      { to: '/retainers', label: 'Retainers', perm: 'retainer.read', icon: '🏦' },
      { to: '/contractor-payments', label: 'Contractor Payments', perm: 'contractor_payment.read', icon: '🧰' },
      { to: '/vendors', label: 'Vendors', perm: 'vendor.read', icon: '🏪' },
      { to: '/financial-reports', label: 'Reports', perm: 'expense.read', icon: '📊' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/compliance', label: 'Compliance Dashboard', perm: 'compliance.read', icon: '⚖' },
      { to: '/compliance/gap', label: 'Gap Analysis', perm: 'compliance.read', icon: '🧭' },
      { to: '/compliance/licenses', label: 'Company Licenses', perm: 'compliance.read', icon: '📜' },
      { to: '/compliance/insurance', label: 'Insurance & Bonds', perm: 'compliance.read', icon: '🛡' },
      { to: '/compliance/catalog', label: 'Requirements Catalog', perm: 'compliance.read', icon: '🗺' },
      { to: '/compliance/roster', label: 'Roster Compliance', perm: 'compliance.read', icon: '👥' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/documents', label: 'Documents', perm: 'document.read', icon: '🗂' },
      { to: '/report-templates', label: 'Report Templates', perm: 'report_template.read', icon: '🧩' },
    ],
  },
  {
    label: 'DSR Admin',
    items: [
      { to: '/admin/users', label: 'User Accounts', perm: 'settings.manage_users', icon: '👥' },
      { to: '/admin/roles', label: 'Roles & Permissions', perm: 'settings.manage_rbac', icon: '🔐' },
    ],
  },
  {
    label: 'Applicant',
    items: [
      { to: '/portal/application', label: 'My Application', perm: 'portal.read_status', icon: '📨' },
    ],
  },
];

export interface NavMatch {
  section: NavSection;
  item: NavItem;
}

/**
 * Resolve the active section+item from a pathname using longest-prefix matching
 * with segment-boundary safety. `/app` matches exactly (so it never claims
 * `/app/invoices`); every other item matches `pathname === to ||
 * pathname.startsWith(to + '/')`, and the longest matching `to` wins. Returns
 * null when nothing matches (e.g. /settings, /force-password-change).
 *
 * Pass already-permission-filtered sections so a route the user cannot see can
 * never win the match.
 */
export function findActiveNav(
  pathname: string,
  sections: NavSection[],
): NavMatch | null {
  let best: NavMatch | null = null;
  let bestLen = -1;
  for (const section of sections) {
    for (const item of section.items) {
      const to = item.to;
      const hit =
        to === '/app'
          ? pathname === '/app'
          : pathname === to || pathname.startsWith(to + '/');
      if (hit && to.length > bestLen) {
        best = { section, item };
        bestLen = to.length;
      }
    }
  }
  return best;
}
